import React, { useEffect, useState } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { useUsage } from '../context/UsageContext';
import { useAuth } from '../context/AuthContext';
import { chartRequest } from '../api/chartsApi';
import { PageHeading, LoadingState } from '../components/UI';
import { chartPresentation } from '../api/chartPresentation';
import './NatalChartResultPage.css';
import NatalChart from '../components/NatalChart';
import AskGptForm from '../components/AskGptForm'; // Подключаем компонент чата
import { LANDING_RELATIONSHIP_INTENT, isLandingRelationshipIntent, normalizeLandingIntent } from './landingIntent';

const NatalChartResultPage = () => {
  const { chartId: routeChartId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, setActiveGuestChart } = useAuth();
  const { usage } = useUsage();
  const chartId = routeChartId || localStorage.getItem("chart_id") || (() => {
    try { return JSON.parse(localStorage.getItem("natalChart"))?.chart_id; }
    catch { return null; }
  })();
  const token = localStorage.getItem("access_token");
  const sessionToken = localStorage.getItem("session_token");
  const chartAuth = String(location.state?.chartAuth?.chartId) === String(chartId) ? location.state.chartAuth : null;
  const landingIntent = normalizeLandingIntent(location.state?.landingIntent);
  const relationshipIntent = isLandingRelationshipIntent(location.state?.landingRelationshipIntent);
  const pendingQuestion = chartAuth?.pendingQuestion || landingIntent?.question || "";
  const unsaved = Boolean(chartAuth && chartAuth.status !== "migrated");
  const guestSessionToken = unsaved ? chartAuth.sessionToken : undefined;
  const migrationNotice = chartAuth?.status === "limit_reached" ? `Вы вошли в аккаунт, но карта не сохранена: достигнут лимит сохранённых карт${usage ? ` (${usage.saved_charts_limit})` : ""}. Откройте «Мои карты», чтобы управлять сохранёнными картами.` :
    chartAuth?.status === "not_found" ? "Вход выполнен, но гостевую карту не удалось перенести. Она не считается сохранённой." :
    chartAuth?.status === "not_requested" ? "Вход выполнен. Перенос карты не выполнялся; карта не сохранена в аккаунт." :
    chartAuth?.status === "migrated" ? "Карта сохранена в вашем аккаунте." : "";
  const identityKey = `${token || ""}:${sessionToken || ""}:${user?.id || ""}:${guestSessionToken || ""}`;
  const requestKey = `${identityKey}:${chartId}`;
  const [loaded, setLoaded] = useState(null);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const chartData = loaded?.key === requestKey ? loaded.data : null;

  useEffect(() => {
    if (chartData && !user && (!token || token === "null") && sessionToken) {
      setActiveGuestChart?.({ chartId: chartData.chart_id, sessionToken, pendingQuestion,
        ...(relationshipIntent ? { landingRelationshipIntent: LANDING_RELATIONSHIP_INTENT } : {}) });
    }
    return () => setActiveGuestChart?.(null);
  }, [chartData, user, token, sessionToken, pendingQuestion, relationshipIntent, setActiveGuestChart]);

  useEffect(() => {
    const controller = new AbortController();
    setLoaded(null);
    setError(null);
    if (!/^[1-9]\d*$/.test(String(chartId))) {
      setError(new Error("Карта не выбрана. Создайте карту или откройте её в разделе «Мои карты»."));
      return () => controller.abort();
    }
    chartRequest(`/natal-chart/${chartId}`, { signal: controller.signal, ...(guestSessionToken ? { guestSessionToken } : {}) })
      .then(data => { if (!controller.signal.aborted) setLoaded({ key: requestKey, data }); })
      .catch(err => { if (!controller.signal.aborted) setError(err); });
    return () => controller.abort();
  }, [chartId, requestKey, reload, guestSessionToken]);

  const consumeQuestion = () => {
    if (pendingQuestion) navigate(location.pathname, { replace: true, state: {
      ...location.state,
      ...(chartAuth ? { chartAuth: { ...chartAuth, pendingQuestion: "" } } : {}),
      landingIntent: null,
    } });
  };

  if (error) {
    return <div className="page result-error">
      {migrationNotice && <p role="status">{migrationNotice}</p>}
      <p role="alert">{error.message}</p>
      <button onClick={() => setReload(value => value + 1)}>Повторить</button>
      <p><Link to="/my-charts">Мои карты</Link> · <Link to="/try-free">Создать карту</Link></p>
      {error.status === 401 && <Link to="/authorization">Войти</Link>}
    </div>;
  }

  if (!chartData) {
    return <div className="page"><LoadingState text="Загрузка натальной карты..." /></div>;
  }

  return (
    <div className="page result-page">
   <PageHeading eyebrow="Ваш личный космос" title={relationshipIntent ? "Первая карта готова" : landingIntent ? "Ваша карта готова" : chartPresentation(chartData.chart_id).name || `Натальная карта №${chartData.chart_id}`}
     action={relationshipIntent ? null : <a className="button" href="#chart-conversation">Перейти к разговору ↓</a>}>
     <p>{relationshipIntent ? "Теперь можно сохранить её и добавить карту другого человека для разбора отношений." :
       landingIntent ? "Теперь можно вернуться к вопросу, с которого вы начали." : "Карта — отправная точка. Вы выбираете, о чём поговорить."}</p>
     {chartData.timezone && <p className="result-metadata">Часовой пояс рождения: {chartData.timezone}</p>}
   </PageHeading>
   {Array.isArray(chartData.calculation_warnings) && chartData.calculation_warnings.length > 0 &&
     <aside className="notice" role="status" aria-label="Неполные данные карты">
       {chartData.calculation_warnings.map((warning, index) => <p key={index}>{warning}</p>)}
     </aside>}
   {relationshipIntent && <aside className="notice" aria-label="Следующий шаг для разбора отношений">
     <p>{user ? "Первая карта готова. Теперь добавьте карту другого человека, чтобы исследовать динамику ваших отношений." :
       "Первая карта готова. Сохраните её в аккаунте, затем добавьте карту другого человека для разбора отношений."}</p>
     {user ? <Link to="/relationships/new">Продолжить разбор отношений →</Link> :
       <Link to="/authorization" state={{ mode: "register", returnTo: `/natal-chart-result/${chartData.chart_id}`,
         guestChart: { chartId: chartData.chart_id, sessionToken, pendingQuestion,
           landingRelationshipIntent: LANDING_RELATIONSHIP_INTENT } }}>Сохранить карту и продолжить →</Link>}
   </aside>}
   {migrationNotice && <p role="status" className="notice">{migrationNotice} <Link to="/my-charts">Мои карты</Link></p>}
   <NatalChart
  key={requestKey}
  chartId={chartData.chart_id}
  bodies={chartData.bodies_for_circle}
  aspects={chartData.aspects_for_circle}
  pointsData={chartData.points_data}
  patterns={chartData.patterns_data}
  structuredAspects={chartData.aspects_structured}
  houses={chartData.houses}
  houseSystem={chartData.house_system}
>
  {/* ✅ Передаём чат как children */}
  <section id="chart-conversation" className="result-conversation" aria-label="Разговор о вашей карте">
    <AskGptForm chartId={chartData.chart_id} unsaved={unsaved} initialQuestion={pendingQuestion}
      guestSessionToken={sessionToken} onQuestionConsumed={consumeQuestion} />
  </section>
</NatalChart>

   
    </div>
  );
  
};

export default NatalChartResultPage;
